// MaturaLinearShiftClosedTask.js
// Zadanie zamknięte: przesunięcie wykresu funkcji liniowej
// f(x) = ax, przesunięcie o p jednostek po OX lub po OY.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

// losowanie
const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const LETTERS = ["A", "B", "C", "D"];

// =====================================
// GENERATOR
// =====================================
function createJune2025OriginalTask() {
  return {
    type: "MaturaLinearShiftClosedTask",
    data: {
      a: 5,
      p: 1,
      direction: "right",
      options: [
        { label: "A", val: -5 },
        { label: "B", val: -1 },
        { label: "C", val: 1 },
        { label: "D", val: 5 },
      ],
      correctIndex: 0,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 12,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return createJune2025OriginalTask();
  }

  // współczynnik kierunkowy 2..5, wyraz wolny = 0
  const a = randInt(2, 5);

  // liczba jednostek przesunięcia (dodatnia)
  const p = randInt(1, 4);

  // kierunek: tylko po jednej osi
  const directions = ["right", "left", "up", "down"];
  const direction = directions[randInt(0, directions.length - 1)];

  // wartości wyrazu wolnego w odpowiedziach:
  // A: w prawo  (x - p)  -> ax - ap
  // B: w dół    (y - p)  -> ax - p
  // C: w górę   (y + p)  -> ax + p
  // D: w lewo   (x + p)  -> ax + ap
  const valRight = -a * p;
  const valDown = -p;
  const valUp = p;
  const valLeft = a * p;

  const options = [
    { label: "A", val: valRight },
    { label: "B", val: valDown },
    { label: "C", val: valUp },
    { label: "D", val: valLeft },
  ];

  let correctIndex;
  switch (direction) {
    case "right":
      correctIndex = 0; // A
      break;
    case "down":
      correctIndex = 1; // B
      break;
    case "up":
      correctIndex = 2; // C
      break;
    case "left":
    default:
      correctIndex = 3; // D
      break;
  }

  return {
    type: "MaturaLinearShiftClosedTask",
    data: {
      a,
      p,
      direction,
      options,
      correctIndex,
    },
  };
}

// =====================================
// TREŚĆ
// =====================================
export function renderTask({ data }) {
  const { a, p, direction, options } = data;

  const fLatex = `f(x) = ${a}x`;
  const pLatex = `${p}`;
  const units = p === 1 ? "jednostkę" : "jednostki";

  let dirSentence;
  if (direction === "right") {
    dirSentence =
      <>
        przesunięto o <InlineMath math={pLatex} /> {units} w prawo wzdłuż osi <InlineMath math="Ox" />
      </>;
  } else if (direction === "left") {
    dirSentence =
      <>
        przesunięto o <InlineMath math={pLatex} /> {units} w lewo wzdłuż osi <InlineMath math="Ox" />
      </>;
  } else if (direction === "up") {
    dirSentence =
      <>
        przesunięto o <InlineMath math={pLatex} /> {units} w górę wzdłuż osi <InlineMath math="Oy" />
      </>;
  } else {
    // "down"
    dirSentence =
      <>
        przesunięto o <InlineMath math={pLatex} /> {units} w dół wzdłuż osi <InlineMath math="Oy" />
      </>;
  }

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p><strong>Zadanie maturalne – zamknięte.</strong></p>

      <p>
        Funkcja liniowa <InlineMath math={'f'} /> jest określona wzorem{" "}
        <InlineMath math={fLatex} />.
      </p>

      <p>
        W kartezjańskim układzie współrzędnych{" "}
        wykres funkcji <InlineMath math={'f'} />{" "}
        {dirSentence}. W wyniku tego przesunięcia otrzymano wykres
        funkcji liniowej <InlineMath math={'g'} />.
      </p>

      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>

      <p>
        Funkcja <InlineMath math={'g'} /> jest określona wzorem:
      </p>

      {options.map((o, i) => {
        const gLatex = `g(x) = ${a}x ${o.val >= 0 ? "+ " + o.val : o.val}`;
        return (
          <p key={i}>
            <InlineMath math={`${o.label}.\\; ${gLatex}`} />
          </p>
        );
      })}
    </div>
  );
}

// =====================================
// INPUT
// =====================================
export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options, a } = data;

  const selected =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((o, i) => ({
    value: i,
    label: o.label,
    textNode: o.label,
  }));

  const handleChange = (i) =>
    onChange?.({
      choiceIndex: i,
      label: options[i].label,
      val: options[i].val,
      a, // dodajemy, żeby renderUserAnswer miał wszystko bez ctx/data
    });

  return (
    <ABCDInput
      options={letterOptions}
      value={selected}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// =====================================
// CHECK
// =====================================
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

// =====================================
// ANSWER
// =====================================
export function renderAnswer({ data }) {
  const { options, correctIndex, a } = data;
  const correct = options[correctIndex];
  const gLatex = `g(x) = ${a}x ${correct.val >= 0 ? "+ " + correct.val : correct.val}`;

  return (
    <p>
      <InlineMath math={`${correct.label}.\\; ${gLatex}`} />
    </p>
  );
}

// =====================================
// EXPLANATION
// =====================================
export function renderExplanation({ data }) {
  const { a, p, direction } = data;

  const pLatex = `${p}`;

  // wektor przesunięcia (p, q)
  // poziomo: (±p, 0), pionowo: (0, ±p)
  const px =
    direction === "right" ? p : direction === "left" ? -p : 0;
  const qy =
    direction === "up" ? p : direction === "down" ? -p : 0;

  const pxLatex = `${px}`;
  const qyLatex = `${qy}`;

  const isHorizontal = direction === "right" || direction === "left";

  // podstawienie do wzoru ogólnego: x -> x - p_x, + q_y
  const xSub =
    px === 0
      ? "x"
      : px > 0
      ? `x-${px}`
      : `x+${Math.abs(px)}`;

  const qTerm =
    qy === 0
      ? ""
      : qy > 0
      ? `+${qy}`
      : `-${Math.abs(qy)}`;

  // finalne rachunki dla konkretnego a i p
  const gFinal =
    isHorizontal
      ? `g(x) = f(${xSub})`
      : `g(x) = f(x) ${qTerm}`;

  const gPlug =
    isHorizontal
      ? `g(x) = ${a}(${xSub})`
      : `g(x) = ${a}x ${qTerm}`;

  let gExpanded;
  if (isHorizontal) {
    const c = -a * px; // bo a(x - px) = ax - a*px
    gExpanded = `g(x) = ${a}x ${c >= 0 ? `+ ${c}` : `- ${Math.abs(c)}`}`;
  } else {
    const c = qy;
    gExpanded = `g(x) = ${a}x ${c >= 0 ? `+ ${c}` : `- ${Math.abs(c)}`}`;
  }

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Zapisujemy funkcję wyjściową:
      </p>
      <BlockMath math={`f(x) = ${a}x`} />

      <p>
        <strong>Krok 2.</strong> Reguła ogólna: jeśli wykres funkcji{" "}
        <InlineMath math={'y=f(x)'} /> przesuniemy o wektor{" "}
        <InlineMath math={'(p,q)'} />, to nowa funkcja ma postać:
      </p>
      <BlockMath math={`g(x) = f(x - p) + q`} />

      <p>
        W tym zadaniu przesunięcie odpowiada wektorowi{" "}
        <InlineMath math={`(p,q)=(${pxLatex},\\,${qyLatex})`} />.
      </p>

      <p>
        <strong>Krok 3.</strong> Podstawiamy do wzoru{" "}
        <InlineMath math={'g(x) = f(x-p) + q'} /> i upraszczamy:
      </p>

      {/* pokazujemy „ładnie” minus minus przy ruchu w lewo */}
      {isHorizontal ? (
        <>
          <BlockMath math={`g(x) = f\\big(x - (${pxLatex})\\big) + ${qyLatex}`} />
          <BlockMath math={gFinal} />
          <BlockMath math={gPlug} />
          <BlockMath math={gExpanded} />
        </>
      ) : (
        <>
          <BlockMath math={`g(x) = f(x - 0) + (${qyLatex})`} />
          <BlockMath math={gFinal} />
          <BlockMath math={gPlug} />
          <BlockMath math={gExpanded} />
        </>
      )}

      <p>
        To dokładnie odpowiada jednej z podanych odpowiedzi.
      </p>
    </div>
  );
}



// =====================================
// ODPOWIEDŹ UCZNIA
// =====================================
export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") {
    return <i>(brak odpowiedzi)</i>;
  }

  const label = userAnswer.label ?? "?";
  const a = userAnswer.a;
  const val = userAnswer.val;

  // jakby ktoś miał stare zapisy bez val/a — pokaż chociaż literkę w KaTeX
  if (typeof a !== "number" || typeof val !== "number") {
    return (
      <div style={{ marginTop: "1rem", textAlign: "left" }}>
        <span>Twoja odpowiedź: </span>
        <InlineMath math={`${label}.`} />
      </div>
    );
  }

  const gLatex = `g(x) = ${a}x ${val >= 0 ? "+ " + val : val}`;

  return (
    <div style={{ marginTop: "1rem", textAlign: "left" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${gLatex}`} />
    </div>
  );
}

